import { Board } from "../board";
import { Squid } from "../mobs/squid";
import { BigContainer } from "../things/bigContainer";
import { CaveRock } from "../things/caveRock";
import { CaveFloor } from "../things/floors/caveFloor";
import { ConcreteFloor } from "../things/floors/concreteFloor";
import { Floor } from "../things/floors/floor";
import { KeyLootBox } from "../things/keyLootBox";
import { LadderUp } from "../things/ladderUp";
import { MetalBlock } from "../things/metalBlock";
import { MetalLootBox } from "../things/metalLootBox";
import { RareItemsLootBox } from "../things/rareItemsLootBox";
import { WoodenDoor } from "../things/woodenDoor";
import { MiningRobot } from "../things/miningRobot";
import { Portal2 } from "../things/portal2/portal2";
import { Portal2Part } from "../things/portal2/portal2Part";
import { MobSpawnerConfig } from "../things/portal2/mobSpawnerConfig";
import { Ammo } from "../items/ammo";
import { XY } from "../xy";
import { generateWithRetries } from "./levelGenerator";
import { placeNxNMultiTileThingAt, populateLootBoxes, assignGoldToPrePlacedMobs } from "./levelGeneratorUtils";
import { BOARD_WIDTH, BOARD_HEIGHT } from "../gameConfig";

const PH = new XY(0, 0);

export function generateBranch3xbLevel(levelNumber: number, rng: () => number = Math.random): Board {
	return generateWithRetries(levelNumber, () => runGeneration(levelNumber, rng));
}

function runGeneration(levelNumber: number, rng: () => number): Board {
	const board = new Board(BOARD_WIDTH, BOARD_HEIGHT, rng);

	// --- CaveFloor layout (temporary markers for passable areas) ---
	// These define the cave passages. They are removed at the end;
	// the defaultFloor visual renders cave floor everywhere.
	const caveFloorPositions: [number, number][] = [
		// Upper area — entrance from level 10000
		[36, 8],
		[35, 8],
		[34, 7],
		[35, 7],
		[36, 7],
		[37, 7],
		[34, 6],
		[35, 6],
		[36, 6],
		[37, 6],
		[35, 5],
		[36, 5],
		[34, 4],
		[35, 4],
		[36, 4],
		[34, 3],
		[35, 3],
		[36, 3],
		[34, 5],
		[37, 5],
		[37, 4],
		[37, 3],
		[35, 2],
		[36, 2],
		[34, 2],
		[33, 3],
		[33, 4],
		[33, 5],
		[33, 6],
		[33, 7],
		[32, 6],
		[32, 5],
		[32, 4],
		[38, 4],
		[38, 5],
		[38, 6],
		// Rare item room surroundings
		[4, 23],
		[5, 23],
		[6, 23],
		[3, 23],
		[2, 24],
		[2, 23],
		[2, 25],
		[3, 22],
		[4, 21],
		[4, 22],
		[5, 22],
		[6, 22],
		[7, 23],
		[7, 22],
		[5, 21],
		[6, 21],
		[7, 21],
		[8, 24],
		[8, 25],
		[8, 23],
		[9, 23],
		[10, 23],
		[9, 24],
		[10, 24],
		[11, 24],
		[8, 22],
		[9, 22],
		[10, 22],
		[8, 21],
		[9, 21],
		[10, 21],
		[5, 20],
		[6, 20],
		[7, 20],
		[8, 20],
		[9, 20],
		[10, 20],
		[11, 20],
		[12, 20],
		[13, 20],
		[11, 21],
		[12, 21],
		[13, 21],
		[2, 21],
		[3, 21],
		[2, 22],
		[1, 22],
		[1, 24],
		[2, 20],
		[3, 20],
		[4, 20],
		[2, 18],
		[3, 18],
		[4, 18],
		[3, 19],
		[4, 19],
		[5, 19],
		[5, 18],
		[6, 18],
		[7, 18],
		[6, 19],
		[7, 19],
		[8, 19],
		[9, 19],
		[10, 19],
		[11, 19],
		[8, 18],
		[11, 22],
		// Portal 1 area (re-placed over portal)
		[1, 22],
		[2, 22],
		[3, 22],
		[1, 21],
		[2, 21],
		[3, 21],
		[1, 20],
		[2, 20],
		[3, 20],
		// Portal 2 area (re-placed over portal)
		[9, 23],
		[10, 23],
		[11, 23],
		[9, 25],
		[10, 25],
		[11, 25],
		[9, 24],
		[10, 24],
		[11, 24],
		[12, 24],
		[12, 23],
		[8, 26],
		// Portal 3 area (re-placed over portal)
		[3, 17],
		[4, 17],
		[5, 17],
		[3, 18],
		[4, 18],
		[5, 18],
		[3, 19],
		[4, 19],
		[5, 19],
		[2, 19],
		// Long corridor to dead end
		[1, 18],
		[1, 17],
		[1, 16],
		[1, 15],
		[1, 14],
		[1, 13],
		[1, 12],
		[1, 11],
		[2, 11],
		[2, 10],
		[2, 9],
		[1, 9],
		[1, 10],
	];

	for (const [x, y] of caveFloorPositions) {
		board.addThing(new CaveFloor(new XY(x, y)));
	}

	// --- Entrance ladder (back to level 10000) ---
	board.addThing(new LadderUp(new XY(37, 5), levelNumber - 1));

	// --- Big container with 4 DrillLaunchers ---
	const bigContainer = new BigContainer();
	placeNxNMultiTileThingAt(() => bigContainer, board, 2, new XY(33, 6));
	for (let i = 0; i < 4; i++) {
		const metalBox = new MetalLootBox(PH);
		metalBox.addLoot(new Ammo("DrillLauncher", 5));
		bigContainer.addLoot(metalBox);
	}

	// --- Key loot box ---
	board.addThing(new KeyLootBox(new XY(36, 7)));

	// --- Rare item room (TripleSolidifier) ---
	const rareBox = new RareItemsLootBox(new XY(5, 25));
	rareBox.addLoot(new Ammo("TripleSolidifier", 1));
	board.addThing(rareBox);

	// Metal block walls around the room
	for (const [x, y] of [
		[4, 26],
		[5, 26],
		[6, 26],
		[3, 26],
		[3, 25],
		[3, 24],
		[7, 26],
		[7, 25],
		[7, 24],
		[6, 24],
		[4, 24],
	] as [number, number][]) {
		board.addThing(new MetalBlock(new XY(x, y)));
	}

	// Concrete floors inside the room
	board.addThing(new ConcreteFloor(new XY(4, 25)));
	board.addThing(new ConcreteFloor(new XY(5, 25)));
	board.addThing(new ConcreteFloor(new XY(6, 25)));
	board.addThing(new ConcreteFloor(new XY(5, 24)));

	// Door to the room
	board.addThing(new WoodenDoor(new XY(5, 24)));

	// --- Portals (3 squid spawners) ---
	createSquidPortal(board, new XY(2, 21));
	createSquidPortal(board, new XY(10, 24));
	createSquidPortal(board, new XY(4, 18));

	// --- Drill robots (2x2 each) ---
	placeNxNMultiTileThingAt(() => new MiningRobot(), board, 2, new XY(4, 20));
	placeNxNMultiTileThingAt(() => new MiningRobot(), board, 2, new XY(7, 20));

	// --- Extra cave crud at specific spots ---
	board.addThing(new CaveRock(new XY(2, 18)));
	board.addThing(new CaveRock(new XY(2, 19)));

	// --- Default floor visual ---
	board.defaultFloor = "CaveFloor";

	// --- Fill passable cells without a Floor with CaveRock ---
	for (const xy of board.getAllXYs()) {
		const hasFloor = board.getThingsAt(xy).some((t) => t instanceof Floor);
		if (!hasFloor && board.isPassable(xy)) {
			board.addThing(new CaveRock(xy));
		}
	}

	// --- Remove CaveFloor markers ---
	// They served as passable-area markers. The defaultFloor visual
	// renders cave floor on empty cells; ConcreteFloor in the room stays.
	const caveFloors = board.getPhysicalThings().filter((t) => t instanceof CaveFloor);
	for (const f of caveFloors) {
		board.removeThing(f);
	}

	// --- Populate loot boxes (fills KeyLootBox with a key, etc.) ---
	populateLootBoxes(board, null, null, 10, rng);
	assignGoldToPrePlacedMobs(board);
	board.dynamicGoldBudget = 200;

	return board;
}

function createSquidPortal(board: Board, xy: XY): void {
	const portal = new Portal2(xy);
	board.addThing(portal);
	for (const neighborXY of xy.getNeighbors()) {
		if (!board.isInBounds(neighborXY)) continue;
		const part = new Portal2Part(neighborXY);
		board.addThing(part);
		portal.addPart(part);
	}
	const config = new MobSpawnerConfig();
	config.addEntry({ mobType: Squid.key, share: 1 });
	portal.setMobSpawnerConfig(config);
}
